import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import DosarInstantaTracker from '../../components/Tracker/DosarInstanta';
import FacturiAsociate from '../../components/Shared/FacturiAsociate';
import { FilterClearBtn, FilterCountHint, FilterPill } from '../../components/Shared/FilterPill';
import { listAllProceduri, updateProcedura } from '../../services/proceduri';
import { getProcTitle } from '../../utils/procStatus';
import '../../pages/Clienti/ClientiPage.css';
import '../../pages/Oficii/OficiuPage.css';
import './TermenePage.css';

const INSTANTE_MAP = {
  tb:       'Tribunalul Bucuresti',
  trib_alt: 'Alt tribunal',
  cab:      'Curtea de Apel Bucuresti',
  ca_alta:  'Alta curte de apel',
  iccj:     'Inalta Curte de Casatie si Justitie',
  jud:      'Judecatorie',
  ue:       'Instanta UE',
  arbitraj: 'Arbitraj / mediere',
};

const LUNI_RO = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'];

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function fmtMonth(ym) {
  if (!ym) return ym;
  const [y, m] = ym.split('-');
  return `${LUNI_RO[parseInt(m, 10) - 1] || m} ${y}`;
}

function getActiveFaza(data) {
  const faze = data.faze || {};
  const cheie = data.fazaCurenta || 'fond';
  return faze[cheie] || faze.fond || Object.values(faze)[0] || {};
}

function buildRows(proceduri) {
  const rows = [];

  for (const proc of proceduri) {
    const d = proc.data || {};
    const denumire = d.denumire || '—';
    // responsabil stores full name strings (not IDs)
    const avocatiNames = d.responsabil || [];

    const activeFaza = getActiveFaza(d);
    const dosarNr = activeFaza.numarDosar || '';
    const tribunal = INSTANTE_MAP[activeFaza.instanta] || activeFaza.instanta || '';

    for (const t of (d.termene || [])) {
      if (!t.data) continue;
      rows.push({
        key: `t_${proc.id}_${t.id || t.data}`,
        date: t.data,
        hour: '',
        tribunal,
        dosarNr,
        denumire,
        clientName: proc.client_name || '',
        avocatiNames,
        rezolutie: t.nota || '',
        source: 'termen',
        procId: proc.id,
      });
    }

    const sync = d.sedinte_sync;
    if (sync && Array.isArray(sync.sedinte)) {
      const syncTribunal = sync.institutie || tribunal;
      for (const s of sync.sedinte) {
        if (!s.data) continue;
        rows.push({
          key: `s_${proc.id}_${s.data}_${s.ora || ''}`,
          date: s.data,
          hour: s.ora || '',
          tribunal: syncTribunal,
          dosarNr,
          denumire,
          clientName: proc.client_name || '',
          avocatiNames,
          rezolutie: s.solutie || '',
          source: 'sedinta',
          procId: proc.id,
        });
      }
    }
  }

  rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  return rows;
}

/* ── Modal ── */
function ProcModal({ proc, onClose, onUpdated, showToast }) {
  const [localData, setLocalData] = useState(proc.data || {});
  const [saving, setSaving] = useState(false);
  const title = getProcTitle(proc.tip_proc, localData);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateProcedura(proc.id, { data: localData });
      onUpdated({ ...proc, data: localData, ...result.procedura });
      showToast('✓ Procedura salvata');
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box op-modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-sticky-header">
          <div className="op-modal-head">
            <div>
              <div className="op-modal-title">{title !== '—' ? title : 'Dosar Instanta'}</div>
              <div className="op-modal-sub">
                <span className="op-modal-client">{proc.client_name}</span>
                <span className="op-modal-sep">·</span>
                <span className="op-modal-tip">Dosar Instanta</span>
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-scroll-body">
          <DosarInstantaTracker data={localData} onChange={setLocalData} procId={proc.id} clientId={proc.client_id} showToast={showToast} />
          <FacturiAsociate procId={proc.id} clientId={proc.client_id} showToast={showToast} />
          <div className="op-modal-footer">
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Se salveaza...' : (
                <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Salveaza</>
              )}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Inchide</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

/* ── Page ── */
export default function TermenePage() {
  const { viewMode } = useAuth();
  const [proceduri, setProceduri] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const { toasts, show: showToast } = useToast();

  const [fSearch, setFSearch] = useState('');
  const [fMonth, setFMonth]   = useState('');
  const [fAvocat, setFAvocat] = useState('');

  useEffect(() => {
    listAllProceduri(['dosar_instanta'])
      .then(d => setProceduri(d.proceduri || []))
      .catch(() => showToast('Eroare la incarcarea datelor'))
      .finally(() => setLoading(false));
  }, [viewMode]); // eslint-disable-line

  const handleUpdated = (updated) => {
    setProceduri(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
  };

  const rows = useMemo(() => buildRows(proceduri), [proceduri]);

  const monthOptions = useMemo(() => {
    const seen = new Set();
    for (const r of rows) {
      const ym = r.date ? r.date.slice(0, 7) : null;
      if (ym) seen.add(ym);
    }
    return [...seen].sort((a, b) => b.localeCompare(a));
  }, [rows]);

  const avocatOptions = useMemo(() => {
    const seen = new Set();
    for (const r of rows) {
      for (const name of r.avocatiNames) if (name) seen.add(name);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = fSearch.toLowerCase();
    return rows.filter(r => {
      if (q && !r.denumire.toLowerCase().includes(q) && !r.dosarNr.toLowerCase().includes(q)) return false;
      if (fMonth && (!r.date || !r.date.startsWith(fMonth))) return false;
      if (fAvocat && !r.avocatiNames.includes(fAvocat)) return false;
      return true;
    });
  }, [rows, fSearch, fMonth, fAvocat]);

  const hasFilters = fSearch || fMonth || fAvocat;
  const selectedProc = proceduri.find(p => p.id === selectedId);

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Termene</h1>
          <span className="count-badge">{filtered.length}</span>
        </div>
      </div>

      <div className="content">
        <div className="filter-bar">
          <input
            className="search-input"
            style={{ minWidth: 220 }}
            placeholder="Cauta dupa denumire sau nr. dosar…"
            value={fSearch}
            onChange={e => setFSearch(e.target.value)}
          />
          <FilterPill value={fMonth} onChange={e => setFMonth(e.target.value)}>
            <option value="">Data</option>
            {monthOptions.map(ym => (
              <option key={ym} value={ym}>{fmtMonth(ym)}</option>
            ))}
          </FilterPill>
          <FilterPill value={fAvocat} onChange={e => setFAvocat(e.target.value)}>
            <option value="">Avocat</option>
            {avocatOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </FilterPill>
          {hasFilters && (
            <FilterClearBtn onClick={() => { setFSearch(''); setFMonth(''); setFAvocat(''); }} />
          )}
          <FilterCountHint count={filtered.length} singular="termen" plural="termene" />
        </div>

        <div className="clients-card">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Nr. Dosar</th>
                <th>Detalii</th>
                <th>Avocat</th>
                <th>Rezolutie</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="op-center-cell">Se incarca...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="op-center-cell">
                  {hasFilters ? 'Niciun termen nu corespunde filtrelor.' : 'Nu exista termene inregistrate.'}
                </td></tr>
              )}
              {filtered.map(r => (
                <tr
                  key={r.key}
                  onClick={() => setSelectedId(r.procId)}
                  className={selectedId === r.procId ? 'row-selected' : ''}
                >
                  <td className="tp-date-cell">
                    <span className="tp-date">{fmtDate(r.date)}</span>
                    {r.tribunal && <span className="tp-meta">{r.tribunal}</span>}
                    {r.hour && <span className="tp-meta">{r.hour}</span>}
                  </td>
                  <td>{r.dosarNr || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                  <td className="op-title-cell">
                    <div className="op-title">{r.denumire}</div>
                    {r.clientName && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{r.clientName}</div>}
                  </td>
                  <td>
                    {r.avocatiNames.length > 0
                      ? r.avocatiNames.join(', ')
                      : <span style={{ color: 'var(--muted)' }}>—</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                      {r.rezolutie
                        ? <span>{r.rezolutie}</span>
                        : <span style={{ color: 'var(--muted)' }}>—</span>
                      }
                      <span className={`op-status ${r.source === 'sedinta' ? 'tp-badge-portal' : 'tp-badge-manual'}`}>
                        {r.source === 'sedinta' ? 'portal' : 'termen manual'}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedProc && (
        <ProcModal
          key={selectedProc.id}
          proc={selectedProc}
          onClose={() => setSelectedId(null)}
          onUpdated={handleUpdated}
          showToast={showToast}
        />
      )}

      <div className="toast-wrap">
        {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
      </div>
    </main>
  );
}
